import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Pill, Plus, Search, Trash2, Download, Send, Loader2, X, ChevronRight,
  ChevronDown, AlertTriangle, StickyNote, FileText, Printer, Sparkles,
  Calendar, Clock, User, MessageSquare, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Time24Input } from "@/components/Time24Input";
import { useAuth } from "@/lib/auth";
import { DateDMYInput } from "@/components/DateDMYInput";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// ---------------- Unified prescription helpers ------------------
// The frontend mirrors backend/rx_util.py so the auto-calc feels instant
// (no round-trip to the server). If a new frequency code is added on the
// backend we still pull the label from GET /prescriptions/frequency-codes.
const DEFAULT_FREQUENCY_CODES = [
  { code: "OD",   label: "Once daily",       per_day: 1   },
  { code: "BD",   label: "Twice daily",      per_day: 2   },
  { code: "TDS",  label: "Thrice daily",     per_day: 3   },
  { code: "QID",  label: "Four times daily", per_day: 4   },
  { code: "HS",   label: "At bedtime",       per_day: 1   },
  { code: "STAT", label: "Immediately",      per_day: 1   },
  { code: "SOS",  label: "As needed (PRN)",  per_day: 0   },
  { code: "QOD",  label: "Alternate days",   per_day: 0.5 },
  { code: "Q4H",  label: "Every 4 hours",    per_day: 6   },
  { code: "Q6H",  label: "Every 6 hours",    per_day: 4   },
  { code: "Q8H",  label: "Every 8 hours",    per_day: 3   },
  { code: "Q12H", label: "Every 12 hours",   per_day: 2   },
];

const firstNumber = (s) => {
  const m = String(s || "").match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
};

/** Local mirror of rx_util.calc_quantity — never returns 0/NaN. */
function calcQuantity({ dosage, frequency, duration }, codes = DEFAULT_FREQUENCY_CODES) {
  if (!frequency) return null;
  const freq = codes.find((c) => c.code === String(frequency).toUpperCase());
  if (!freq || !freq.per_day) return null;
  const days = firstNumber(duration);
  if (!days) return null;
  const dose = firstNumber(dosage) || 1;
  const total = dose * freq.per_day * days;
  return total > 0 ? Math.ceil(total) : null;
}

/** Pull the unit noun from a dosage string ("1 Capsule" → "Capsules"). */
function unitFromDosage(dosage) {
  const m = String(dosage || "").match(/([A-Za-z]+)$/);
  if (!m) return "";
  const unit = m[1];
  return unit.toLowerCase().endsWith("s") ? unit : `${unit}s`;
}


/**
 * RxItemsTable — the unified 7-column viewer used in the expanded rx card.
 * Same columns and order as the PDF template so doctors + patients see the
 * exact same layout on-screen and on paper.
 *
 * Backward compatibility: if a legacy rx (with only `regimen` string) is
 * passed, we display the free-text under a spanned "Regimen" note row.
 */
function RxItemsTable({ items }) {
  if (!items || items.length === 0) {
    return <div className="text-sm text-ink-soft italic">No medicines on this prescription.</div>;
  }
  const hasStructured = items.some((it) => it.dosage || it.frequency || it.duration);
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-[13px] min-w-[720px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-ink-soft border-b border-[#E8EAE9]">
            <th className="py-2 pr-2 w-8">#</th>
            <th className="py-2 pr-3">Drug</th>
            <th className="py-2 pr-3">Strength</th>
            <th className="py-2 pr-3">Dosage</th>
            <th className="py-2 pr-3">Frequency</th>
            <th className="py-2 pr-3">Duration</th>
            <th className="py-2 pr-3">Quantity</th>
            <th className="py-2">Instructions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id || `${it.drug_name}-${i}`} className="border-b border-[#F1EEE7] last:border-b-0 align-top">
              <td className="py-2 pr-2 text-ink-soft">{i + 1}</td>
              <td className="py-2 pr-3">
                <div className="font-medium text-ink">{it.drug_name}</div>
                {it.brand && <div className="text-[11px] text-ink-soft">{it.brand}</div>}
              </td>
              <td className="py-2 pr-3 text-ink-mid">{it.strength || [it.strength, it.form].filter(Boolean).join(" · ") || "—"}</td>
              <td className="py-2 pr-3">{it.dosage || (hasStructured ? "—" : it.regimen || "—")}</td>
              <td className="py-2 pr-3 font-mono text-[12px]">{it.frequency || "—"}</td>
              <td className="py-2 pr-3">{it.duration || "—"}</td>
              <td className="py-2 pr-3 font-medium">{it.quantity || "—"}</td>
              <td className="py-2 text-ink-mid whitespace-pre-wrap break-words">{it.instructions || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ------------------ Drug search popover ------------------
// ------------------ Custom Drug modal (create / edit) ------------------
// Used both from the drug-search popover ("+ Create Custom Drug") and from
// the per-item ⋮ menu ("Edit Drug"). Editing here mutates the clinic
// drug master only — historical prescriptions keep their snapshotted
// drug fields (see backend/routers/prescriptions.py::RxItemIn).
function CustomDrugModal({ open, onOpenChange, initial, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    generic:          "",
    brand:            "",     // stored server-side inside `brands: [brand]`
    category:         "Analgesic",
    default_strength: "",
    unit:             "mg",
    form_:            "Tablet",   // reserved word workaround
    route:            "Oral",
    dental_notes:     "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      generic:          initial?.generic || "",
      brand:            (initial?.brands || [])[0] || initial?.brand || "",
      category:         initial?.category || "Analgesic",
      default_strength: initial?.default_strength || "",
      unit:             initial?.unit || "mg",
      form_:            (initial?.forms || [])[0] || initial?.form || "Tablet",
      route:            initial?.route || "Oral",
      dental_notes:     initial?.dental_notes || "",
    });
  }, [open, initial]);

  const save = async () => {
    if (!form.generic.trim()) {
      toast.error("Drug name is required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        generic:          form.generic.trim(),
        brands:           form.brand.trim() ? [form.brand.trim()] : [],
        category:         form.category,
        default_strength: form.default_strength.trim() || null,
        forms:            [form.form_],
        route:            form.route,
        dental_notes:     form.dental_notes.trim() || null,
      };
      const { data } = isEdit
        ? await api.put(`/drugs/custom/${initial.id}`, payload)
        : await api.post(`/drugs/custom`, payload);
      if (data.duplicate) {
        toast.info("This drug already exists in your clinic database. Using existing.");
      } else {
        toast.success(isEdit ? "Custom drug updated." : "Custom drug saved.");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="custom-drug-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Custom Drug" : "Create Custom Drug"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">Drug Name <span className="text-red-500">*</span></label>
            <Input value={form.generic} onChange={(e) => setForm({ ...form, generic: e.target.value })}
                    placeholder="e.g. Dentocalm" data-testid="cd-generic" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">Brand</label>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                      placeholder="Brand Name" data-testid="cd-brand" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">Category</label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      placeholder="Analgesic, Antibiotic…" data-testid="cd-category" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">Strength</label>
              <Input value={form.default_strength}
                      onChange={(e) => setForm({ ...form, default_strength: e.target.value })}
                      placeholder="400" data-testid="cd-strength" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">Unit</label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      placeholder="mg" data-testid="cd-unit" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">Dosage Form</label>
              <Input value={form.form_} onChange={(e) => setForm({ ...form, form_: e.target.value })}
                      placeholder="Tablet" data-testid="cd-form" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">Route</label>
            <Input value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })}
                    placeholder="Oral" data-testid="cd-route" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">Notes</label>
            <Textarea rows={2} value={form.dental_notes}
                        onChange={(e) => setForm({ ...form, dental_notes: e.target.value })}
                        placeholder="Dental notes / warnings" data-testid="cd-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}
                     data-testid="cd-cancel">Cancel</Button>
            <Button type="button" onClick={save} disabled={saving}
                     data-testid="cd-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save Changes" : "Save Drug")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function DrugSearch({ onPick, allergies }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/drugs/search`, { params: { q, limit: 20 } });
        if (!cancelled) setResults(data.drugs || []);
      } catch (e) {
        // silently ignore; popover just shows empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  const isRisky = (d) => {
    if (!allergies) return false;
    const al = allergies.toLowerCase();
    return [d.generic, ...(d.brands || [])]
      .some((n) => n && al.includes(n.toLowerCase().split(" ")[0]));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="rx-add-drug-btn"
          className="border-pine/20 text-ink hover:bg-linen"
        >
          <Plus className="w-4 h-4 mr-1" /> Add drug
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] p-0" data-testid="rx-drug-search-popover">
        <div className="p-3 border-b border-[#E2DCD0]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search generic, brand, category…"
              className="pl-9"
              data-testid="rx-drug-search-input"
            />
          </div>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8 text-ink-soft">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="text-center text-sm text-ink-soft py-10 px-4">
              No drugs found. Try another term.
            </div>
          )}
          {!loading && results.map((d) => {
            const risky = isRisky(d);
            return (
              <button
                key={d.slug || d.id}
                type="button"
                onClick={() => { onPick(d); setOpen(false); setQ(""); }}
                data-testid={`rx-drug-result-${d.slug || d.id}`}
                className="w-full text-left px-4 py-3 hover:bg-linen border-b border-[#F0EBE0] last:border-b-0 transition"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-ink">{d.generic}</div>
                  <div className="flex items-center gap-1">
                    {risky && (
                      <Badge variant="outline" className="border-red-500 text-red-600 text-[10px]">
                        <AlertTriangle className="w-3 h-3 mr-0.5" /> Allergy?
                      </Badge>
                    )}
                    {d.source === "custom" && (
                      <Badge variant="outline" className="text-[10px]">Custom</Badge>
                    )}
                  </div>
                </div>
                <div className="text-xs text-ink-mid mt-0.5">
                  {(d.brands || []).slice(0, 3).join(", ")}{(d.brands || []).length > 3 ? "…" : ""}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-ink-soft mt-1">
                  <span className="uppercase tracking-wider">{d.category}</span>
                  {d.default_strength && <span>· {d.default_strength}</span>}
                  {d.pregnancy_category && <span>· Preg {d.pregnancy_category}</span>}
                </div>
                {d.dental_notes && (
                  <div className="text-[11px] text-ink-mid mt-1 line-clamp-2 italic">{d.dental_notes}</div>
                )}
              </button>
            );
          })}
        </div>
        {/* Create Custom Drug entry-point — persists to clinic master. */}
        <div className="p-2 border-t border-[#E2DCD0] bg-linen/40">
          <Button type="button" variant="ghost" size="sm"
                   className="w-full justify-start text-pine"
                   onClick={() => { setOpen(false); setCreateOpen(true); }}
                   data-testid="rx-open-create-custom-drug">
            <Plus className="w-4 h-4 mr-1" /> Create Custom Drug
          </Button>
        </div>
      </PopoverContent>
      <CustomDrugModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        initial={null}
        onSaved={(drug) => { onPick(drug); setQ(""); }}
      />
    </Popover>
  );
}

// ------------------ Next Appointment (Optional) — collapsible ------------------
const RX_APPT_PURPOSES = [
  "Review", "Follow-up", "RCT Sitting", "Crown Try-in", "Crown Cementation",
  "Extraction Review", "Implant Review", "Scaling Review", "Dressing Change",
  "Suture Removal", "Consultation", "Emergency", "Other",
];

const RX_APPT_PRESETS = [
  { key: "tomorrow", label: "Tomorrow", days: 1 },
  { key: "3d",       label: "3 Days",   days: 3 },
  { key: "1w",       label: "1 Week",   days: 7 },
  { key: "2w",       label: "2 Weeks",  days: 14 },
  { key: "1m",       label: "1 Month",  days: 30 },
  { key: "3m",       label: "3 Months", days: 90 },
  { key: "custom",   label: "Custom",   days: null },
];

function _addDays(d, n) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
}

function _toDateInput(d) {
  return d.toISOString().slice(0, 10);
}

function _toTimeInput(d) {
  return d.toTimeString().slice(0, 5);
}

function _combineISO(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return new Date(`${dateStr}T${timeStr}:00`).toISOString();
}

/** Optional collapsible follow-up appointment section shown in the Rx form. */
function NextAppointmentSection({ patientId, currentDoctorId, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("custom");
  const [team, setTeam] = useState([]);
  const [conflict, setConflict] = useState(null);   // future appt on this patient
  const now = useMemo(() => new Date(), []);
  const minDate = _toDateInput(now);

  // Load team (doctors) only when the section is opened.
  useEffect(() => {
    if (!open || team.length) return;
    api.get("/team").then((r) => {
      const doctors = (r.data || []).filter(
        (u) => u.role === "doctor" || u.role === "admin",
      );
      setTeam(doctors);
      if (!value.doctor_id) onChange({ ...value, doctor_id: currentDoctorId });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Detect existing future appointment for this patient (client-side filter).
  useEffect(() => {
    if (!open || !patientId) return;
    api.get("/appointments").then((r) => {
      const nowIso = new Date().toISOString();
      const futures = (r.data || [])
        .filter((a) => a.patient_id === patientId &&
          a.start > nowIso &&
          a.status === "scheduled")
        .sort((a, b) => a.start.localeCompare(b.start));
      setConflict(futures[0] || null);
    }).catch(() => setConflict(null));
  }, [open, patientId]);

  const enable = () => {
    if (!open) {
      setOpen(true);
      // Default: 1 week out at 11:00.
      const d = _addDays(new Date(), 7);
      d.setHours(11, 0, 0, 0);
      setPreset("1w");
      onChange({
        enabled: true,
        date: _toDateInput(d),
        time: _toTimeInput(d),
        duration_min: 30,
        purpose: "Follow-up",
        doctor_id: currentDoctorId,
        notes: "",
        send_reminder: true,
        reminder_channel: "whatsapp",
      });
    } else {
      setOpen(false);
      onChange({ enabled: false });
    }
  };

  const applyPreset = (p) => {
    setPreset(p.key);
    if (p.days == null) return; // custom
    const d = _addDays(new Date(), p.days);
    d.setHours(11, 0, 0, 0);
    onChange({ ...value, date: _toDateInput(d), time: _toTimeInput(d) });
  };

  const isPast = () => {
    if (!value.date || !value.time) return false;
    return new Date(`${value.date}T${value.time}:00`) < new Date();
  };

  return (
    <div className="rounded-2xl border border-[#C8DBCE] bg-[#F4F8F4] overflow-hidden transition-all"
         data-testid="rx-next-appt-section">
      <button
        type="button"
        onClick={enable}
        data-testid="rx-next-appt-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#EBF1EB] transition"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-ink" />
          <span className="font-medium text-ink text-sm">
            Next Appointment
            <span className="ml-2 text-xs font-normal text-ink-mid">(Optional)</span>
          </span>
          {open && value.date && (
            <Badge variant="outline" className="ml-2 text-[10px] border-pine/30 text-ink">
              {value.date} · {value.time}
            </Badge>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-ink-mid transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[#C8DBCE]/50">
          {conflict && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
                 data-testid="rx-next-appt-conflict">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Patient already has an appointment on{" "}
                <b>{new Date(conflict.start).toLocaleString("en-IN",
                  { dateStyle: "medium", timeStyle: "short" })}</b>.
                Keeping this new one will create a second appointment.
              </div>
            </div>
          )}

          {/* Preset chips */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Quick presets</div>
            <div className="flex flex-wrap gap-1.5" data-testid="rx-next-appt-presets">
              {RX_APPT_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p)}
                  data-testid={`rx-preset-${p.key}`}
                  className={`px-3 py-1 rounded-full text-xs border transition ${
                    preset === p.key
                      ? "bg-pine text-linen border-pine"
                      : "bg-white text-ink border-pine/20 hover:border-pine/40"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft">
                <Calendar className="inline w-3 h-3 mr-1" /> Date
              </label>
              <DateDMYInput
                min={minDate}
                value={value.date || ""}
                onChange={(e) => { setPreset("custom"); onChange({ ...value, date: e.target.value }); }}
                data-testid="rx-next-appt-date"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft">
                <Clock className="inline w-3 h-3 mr-1" /> Time
              </label>
              <Time24Input
                value={value.time || ""}
                onChange={(v) => { setPreset("custom"); onChange({ ...value, time: v }); }}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="rx-next-appt-time"
              />
            </div>
          </div>

          {isPast() && (
            <div className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Date/Time cannot be in the past.
            </div>
          )}

          {/* Purpose + Doctor */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft">Purpose</label>
              <select
                value={value.purpose || "Follow-up"}
                onChange={(e) => onChange({ ...value, purpose: e.target.value })}
                data-testid="rx-next-appt-purpose"
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                {RX_APPT_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft">
                <User className="inline w-3 h-3 mr-1" /> Doctor
              </label>
              <select
                value={value.doctor_id || ""}
                onChange={(e) => onChange({ ...value, doctor_id: e.target.value })}
                data-testid="rx-next-appt-doctor"
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                {team.length === 0 && <option value={currentDoctorId}>Me</option>}
                {team.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-ink-soft">Notes</label>
            <Textarea
              rows={2}
              value={value.notes || ""}
              onChange={(e) => onChange({ ...value, notes: e.target.value })}
              placeholder="Review healing, remove sutures, evaluate pain..."
              data-testid="rx-next-appt-notes"
            />
          </div>

          {/* Reminder */}
          <label className="flex items-center gap-2 text-sm text-ink cursor-pointer"
                 data-testid="rx-next-appt-reminder-label">
            <input
              type="checkbox"
              checked={!!value.send_reminder}
              onChange={(e) => onChange({ ...value, send_reminder: e.target.checked })}
              data-testid="rx-next-appt-reminder"
              className="accent-pine"
            />
            <MessageSquare className="w-3.5 h-3.5" />
            Send appointment reminder via WhatsApp
          </label>
        </div>
      )}
    </div>
  );
}

// ------------------ New Rx modal ------------------
function NewRxDialog({ open, onClose, patient, onSaved }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [nextAppt, setNextAppt] = useState({ enabled: false });
  // Pencil-icon "Edit Drug (clinic master)" button at line ~839 hands the
  // matched drug row into this state. Phase-2 Drug Database will render
  // a DrugEditor modal off this — until then it silently stores the ref
  // so the click handler validates without a runtime ReferenceError.
  const [, setEditDrug] = useState(null);

  useEffect(() => {
    if (open) {
      setItems([]); setDiagnosis(""); setNotes("");
      setNextAppt({ enabled: false });
    }
  }, [open]);

  const addDrug = (drug) => {
    // Try to seed dosage from drug.default_regimen if the drug DB has one.
    // Best-effort: don't fail hard on unparseable defaults.
    let seedDosage = "";
    let seedFrequency = "";
    let seedDuration = "";
    if (drug.default_regimen) {
      const m = /(\d+\s*[A-Za-z]*)\s+(OD|BD|TDS|TID|QID|HS|SOS|QOD|Q4H|Q6H|Q8H|Q12H)\s*[×x]?\s*(\d+\s*(?:day|days|week|weeks|month|months)?)?/i.exec(drug.default_regimen);
      if (m) {
        seedDosage = m[1] || "";
        seedFrequency = m[2]?.toUpperCase() || "";
        seedDuration = m[3] || "";
      }
    }
    const newItem = {
      drug_slug: drug.slug,
      drug_name: drug.generic,
      brand: (drug.brands && drug.brands[0]) || "",
      strength: drug.default_strength || "",
      form: (drug.forms && drug.forms[0]) || "",
      dosage: seedDosage,
      frequency: seedFrequency,
      duration: seedDuration,
      quantity: "",
      qty_manual: false,
      instructions: "",
    };
    // Pre-compute quantity if we have all 3 structured fields.
    const q = calcQuantity(newItem);
    if (q) newItem.quantity = `${q} ${unitFromDosage(newItem.dosage) || "units"}`.trim();
    setItems((prev) => [...prev, newItem]);
  };

  const patch = (idx, k, v) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [k]: v };
      // If the user edits Quantity directly, lock it (manual override).
      if (k === "quantity") next.qty_manual = true;
      // Auto-recalc quantity when dosage / frequency / duration change,
      // UNLESS the doctor has manually overridden it.
      if (["dosage", "frequency", "duration"].includes(k) && !next.qty_manual) {
        const q = calcQuantity(next);
        if (q != null) {
          const unit = unitFromDosage(next.dosage) || "units";
          next.quantity = `${q} ${unit}`.trim();
        }
      }
      return next;
    }));
  };
  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const resetQuantity = (idx) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, qty_manual: false };
      const q = calcQuantity(next);
      next.quantity = q != null ? `${q} ${unitFromDosage(next.dosage) || "units"}`.trim() : "";
      return next;
    }));
  };

  const save = async () => {
    if (items.length === 0) { toast.error("Add at least one drug"); return; }
    if (items.some((i) => !i.drug_name || !i.frequency || !i.duration)) {
      toast.error("Every item needs a drug, frequency, and duration");
      return;
    }
    // Validate follow-up appointment payload if enabled.
    let next_appointment = null;
    if (nextAppt.enabled) {
      const iso = _combineISO(nextAppt.date, nextAppt.time);
      if (!iso) {
        toast.error("Please pick a date and time for the next appointment");
        return;
      }
      if (new Date(iso) < new Date()) {
        toast.error("Next appointment must be in the future");
        return;
      }
      next_appointment = {
        start: iso,
        duration_min: Number(nextAppt.duration_min) || 30,
        purpose: nextAppt.purpose || "Follow-up",
        doctor_id: nextAppt.doctor_id || null,
        notes: nextAppt.notes || null,
        send_reminder: !!nextAppt.send_reminder,
        reminder_channel: nextAppt.reminder_channel || "whatsapp",
      };
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/prescriptions`, {
        diagnosis: diagnosis || null,
        notes: notes || null,
        items,
        next_appointment,
      });
      const suffix = data?.next_appointment ? " + appointment scheduled" : "";
      toast.success(`Prescription ${data.rx_number} saved${suffix}`);
      onSaved(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save prescription");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="new-rx-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-ink flex items-center gap-2">
            <Pill className="w-5 h-5 text-clay" /> New prescription
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-ink-mid">
          For <b className="text-ink">{patient?.name}</b>
          {patient?.clinic_patient_id && (
            <span className="ml-2 text-[10px] font-mono text-ink-soft" data-testid="rx-dialog-cpid">
              ID: {patient.clinic_patient_id}
            </span>
          )}
          {patient?.allergies && (
            <span className="ml-2 inline-flex items-center gap-1 text-red-600">
              <AlertTriangle className="w-3.5 h-3.5" />
              Allergies: <b>{patient.allergies}</b>
            </span>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-ink-soft">Diagnosis / indication</div>
          <Input
            value={diagnosis}
            onChange={(e) => setDiagnosis(e.target.value)}
            placeholder="e.g. Acute apical periodontitis on #16"
            data-testid="rx-diagnosis-input"
          />
        </div>

        {/* Items */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-ink-soft">Medicines</div>
            <DrugSearch onPick={addDrug} allergies={patient?.allergies} />
          </div>

          {items.length === 0 && (
            <div className="text-center py-10 border border-dashed border-[#E2DCD0] rounded-2xl text-ink-soft text-sm">
              No medicines yet. Click <b>+ Add drug</b> to search the dental drug database.
            </div>
          )}

          {items.map((it, idx) => (
            <div key={idx} className="rounded-2xl border border-[#E2DCD0] p-4 bg-white space-y-2"
                 data-testid={`rx-item-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="font-medium text-ink">{it.drug_name}</div>
                  <div className="text-xs text-ink-mid">{it.brand}</div>
                </div>
                <div className="flex items-center gap-1">
                  {/* Edit Drug — only visible for custom drugs. Opens the
                      clinic drug master editor. Historical prescriptions
                      are unaffected (they carry a snapshot). */}
                  {(it.drug_slug || "").startsWith("custom-") && (
                    <Button type="button" size="sm" variant="ghost"
                            data-testid={`rx-item-edit-drug-${idx}`}
                            title="Edit Drug (clinic master)"
                            onClick={async () => {
                              try {
                                const { data } = await api.get(
                                  `/drugs/search`, { params: { q: it.drug_name || "" } });
                                const match = (data.results || data || []).find(
                                  (d) => d.slug === it.drug_slug && d.source === "custom");
                                if (!match) { toast.error("Custom drug not found in master"); return; }
                                setEditDrug(match);
                              } catch { toast.error("Could not load drug"); }
                            }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="ghost"
                          onClick={() => remove(idx)}
                          data-testid={`rx-item-remove-${idx}`}
                          className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <Input value={it.strength || ""} onChange={(e) => patch(idx, "strength", e.target.value)}
                       placeholder="Strength (500 mg)"
                       className="md:col-span-2"
                       data-testid={`rx-item-strength-${idx}`} />
                <Input value={it.dosage || ""} onChange={(e) => patch(idx, "dosage", e.target.value)}
                       placeholder="Dosage (1 Cap)"
                       data-testid={`rx-item-dosage-${idx}`} />
                <select
                  value={it.frequency || ""}
                  onChange={(e) => patch(idx, "frequency", e.target.value)}
                  data-testid={`rx-item-frequency-${idx}`}
                  className="h-10 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="">Frequency</option>
                  {DEFAULT_FREQUENCY_CODES.map((f) => (
                    <option key={f.code} value={f.code}>{f.code} — {f.label}</option>
                  ))}
                </select>
                <Input value={it.duration || ""} onChange={(e) => patch(idx, "duration", e.target.value)}
                       placeholder="Duration (5 Days)"
                       data-testid={`rx-item-duration-${idx}`} />
                <div className="relative">
                  <Input value={it.quantity || ""} onChange={(e) => patch(idx, "quantity", e.target.value)}
                         placeholder="Quantity"
                         className={it.qty_manual ? "pr-14" : ""}
                         data-testid={`rx-item-qty-${idx}`} />
                  {it.qty_manual && (
                    <button type="button"
                            onClick={() => resetQuantity(idx)}
                            title="Reset to auto-calc"
                            data-testid={`rx-item-qty-reset-${idx}`}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-widest text-clay hover:underline">
                      auto
                    </button>
                  )}
                </div>
              </div>
              <Textarea value={it.instructions || ""} onChange={(e) => patch(idx, "instructions", e.target.value)}
                     placeholder="Instructions (e.g. After Meals · Finish full course)"
                     rows={2}
                     data-testid={`rx-item-instructions-${idx}`} />
            </div>
          ))}
        </div>

        {/* Optional next appointment */}
        <NextAppointmentSection
          patientId={patient?.id}
          currentDoctorId={user?.id}
          value={nextAppt}
          onChange={setNextAppt}
        />

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-ink-soft">Doctor's notes (optional)</div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Cautions, follow-up guidance, etc."
            data-testid="rx-notes-input"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="rx-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rx-save-btn"
                  className="bg-pine text-linen hover:bg-pine/90">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pill className="w-4 h-4 mr-1" />}
            Save prescription
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Main card ------------------
export default function PrescriptionsCard({ patientId, patient }) {
  const [rxs, setRxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [gated, setGated] = useState(false);
  const [pdfMenuId, setPdfMenuId] = useState(null);
  // Edit-drug modal state — opens when the doctor picks ⋮ → Edit Drug on
  // a custom drug row. Editing here mutates the clinic drug master;
  // historical prescriptions keep their snapshotted drug fields.
  const [editDrug, setEditDrug] = useState(null);       // master doc | null
  // Active custom letterhead — controls whether the "Custom Letterhead"
  // Rx PDF option is offered in the download menu. Fetched once per mount;
  // silent failure (network / no permission / feature-gated) simply hides
  // the option, matching the fallback behaviour on the backend.
  const [activeLetterhead, setActiveLetterhead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/prescriptions`);
      setRxs(data.prescriptions || []);
      setGated(false);
    } catch (e) {
      if (e?.response?.status === 402) setGated(true);
      else toast.error("Could not load prescriptions");
    } finally {
      setLoading(false);
    }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    api.get("/letterheads/active")
      .then(({ data }) => { if (!cancelled) setActiveLetterhead(data?.letterhead || null); })
      .catch(() => { if (!cancelled) setActiveLetterhead(null); });
    return () => { cancelled = true; };
  }, []);

  const downloadPdf = (rx, variant = "digital") => {
    // The auth cookie is httpOnly, so we can't read it from JS. Instead,
    // rely on the browser to attach it automatically via `credentials: 'include'`.
    const url = `${API}/prescriptions/${rx.id}/pdf?variant=${variant}`;
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("failed"); return r.blob(); })
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(objUrl), 30000);
      })
      .catch(() => toast.error("Could not open PDF"));
  };

  const sendWhatsApp = async (rx) => {
    try {
      const { data } = await api.post(`/prescriptions/${rx.id}/whatsapp`);
      if (data.mocked) toast.success("WhatsApp sent (mocked — configure WhatsApp Business API for live delivery)");
      else toast.success("Prescription sent on WhatsApp");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(typeof detail === "object" ? detail.message : "Upgrade to Clinic plan for WhatsApp send");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send on WhatsApp");
      }
    }
  };

  const remove = async (rx) => {
    if (!window.confirm(`Delete prescription ${rx.rx_number}?`)) return;
    try {
      await api.delete(`/prescriptions/${rx.id}`);
      toast.success("Prescription deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  if (gated) {
    return (
      <div className="rounded-2xl bg-white border border-[#E8EAE9] p-6" data-testid="prescriptions-gated">
        <div className="flex items-center gap-2 mb-2">
          <Pill className="w-5 h-5 text-clay" />
          <div className="font-display text-lg">Prescriptions</div>
        </div>
        <div className="text-sm text-ink-mid">
          Prescriptions are available on the Solo & Clinic plans. Upgrade to unlock.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-[#E8EAE9] p-5" data-testid="prescriptions-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Pill className="w-5 h-5 text-clay" />
          <div className="font-display text-lg">Prescriptions</div>
          {rxs.length > 0 && (
            <Badge variant="outline" className="ml-1 text-[10px]">{rxs.length}</Badge>
          )}
        </div>
        <Button size="sm" onClick={() => setOpenNew(true)}
                data-testid="new-rx-btn"
                className="bg-pine text-linen hover:bg-pine/90">
          <Plus className="w-4 h-4 mr-1" /> New Rx
        </Button>
      </div>

      {loading && (
        <div className="text-sm text-ink-soft py-6 text-center">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {!loading && rxs.length === 0 && (
        <div className="border border-dashed border-[#E2DCD0] rounded-xl py-10 text-center text-sm text-ink-mid">
          <FileText className="w-8 h-8 text-ink-disabled mx-auto mb-2" />
          No prescriptions yet. Click <b>New Rx</b> to write one — 90+ common dental drugs are pre-loaded.
        </div>
      )}

      <div className="space-y-2">
        {rxs.map((rx) => (
          <details key={rx.id} className="group border border-[#E8EAE9] rounded-xl bg-linen/40"
                   data-testid={`rx-row-${rx.id}`}>
            <summary className="list-none cursor-pointer p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <ChevronRight className="w-4 h-4 text-ink-soft group-open:rotate-90 transition-transform flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink truncate">
                    {rx.rx_number} · {rx.items?.length || 0} item{(rx.items?.length || 0) === 1 ? "" : "s"}
                  </div>
                  <div className="text-[11px] text-ink-soft truncate">
                    {fmtDate(rx.date || rx.created_at)}
                    {rx.clinic_patient_id && ` · ID: ${rx.clinic_patient_id}`}
                    {rx.doctor_name && ` · ${rx.doctor_name}`}
                    {rx.diagnosis && ` · ${rx.diagnosis}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.preventDefault()}>
                <Popover open={pdfMenuId === rx.id} onOpenChange={(o) => setPdfMenuId(o ? rx.id : null)}>
                  <PopoverTrigger asChild>
                    <Button
                      size="icon" variant="ghost"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setPdfMenuId(pdfMenuId === rx.id ? null : rx.id); }}
                      title="Print / Download prescription"
                      data-testid={`rx-pdf-${rx.id}`}
                      className="h-8 w-8">
                      <Download className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-64 p-1"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`rx-pdf-menu-${rx.id}`}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPdfMenuId(null); downloadPdf(rx, "digital"); }}
                      data-testid={`rx-pdf-digital-${rx.id}`}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-linen transition flex items-start gap-2.5">
                      <Sparkles className="w-4 h-4 text-clay mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-ink">Premium digital</div>
                        <div className="text-[11px] text-ink-mid">Full-branded PDF for portal, WhatsApp &amp; email</div>
                      </div>
                    </button>
                    {activeLetterhead && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPdfMenuId(null); downloadPdf(rx, "custom_letterhead"); }}
                        data-testid={`rx-pdf-custom-letterhead-${rx.id}`}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-linen transition flex items-start gap-2.5">
                        <FileText className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-ink truncate">Custom Letterhead</div>
                          <div className="text-[11px] text-ink-mid truncate">
                            Overlays Rx onto <span className="italic">{activeLetterhead.name}</span>
                          </div>
                        </div>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPdfMenuId(null); downloadPdf(rx, "letterhead"); }}
                      data-testid={`rx-pdf-letterhead-${rx.id}`}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-linen transition flex items-start gap-2.5">
                      <Printer className="w-4 h-4 text-ink mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-ink">Printable letterhead</div>
                        <div className="text-[11px] text-ink-mid">Content only — for pre-printed clinic pads</div>
                      </div>
                    </button>
                  </PopoverContent>
                </Popover>
                <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); e.preventDefault(); sendWhatsApp(rx); }}
                        title="Send WhatsApp" data-testid={`rx-wa-${rx.id}`}
                        className="h-8 w-8">
                  <Send className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); e.preventDefault(); remove(rx); }}
                        title="Delete" data-testid={`rx-del-${rx.id}`}
                        className="h-8 w-8 text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </summary>
            <div className="border-t border-[#E8EAE9] px-4 py-3 space-y-2">
              <RxItemsTable items={rx.items || []} />
              {rx.notes && (
                <div className="border-t border-[#E8EAE9] pt-2 mt-2 flex gap-2 text-xs text-ink-mid">
                  <StickyNote className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>{rx.notes}</div>
                </div>
              )}
              {rx.next_appointment?.start && (
                <div className="border-t border-[#E8EAE9] pt-2 mt-2 flex items-start gap-2 text-xs"
                     data-testid={`rx-next-appt-badge-${rx.id}`}>
                  <Calendar className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-ink" />
                  <div className="text-ink">
                    Next appointment scheduled:{" "}
                    <b>{new Date(rx.next_appointment.start).toLocaleString("en-IN",
                        { dateStyle: "medium", timeStyle: "short" })}</b>
                    {rx.next_appointment.purpose && <> · {rx.next_appointment.purpose}</>}
                    {rx.next_appointment.doctor_name && <> · with {rx.next_appointment.doctor_name}</>}
                  </div>
                </div>
              )}
            </div>
          </details>
        ))}
      </div>

      <NewRxDialog open={openNew} onClose={() => setOpenNew(false)}
                   patient={patient || { id: patientId }}
                   onSaved={() => load()} />

      {/* Edit clinic drug master — only opens when the user picked
          ⋮ → Edit Drug on a custom drug row. Saving here does NOT
          modify historical prescriptions; the Rx item snapshot is
          preserved server-side. */}
      <CustomDrugModal
        open={Boolean(editDrug)}
        onOpenChange={(v) => { if (!v) setEditDrug(null); }}
        initial={editDrug}
        onSaved={() => { setEditDrug(null); load(); }}
      />
    </div>
  );
}
